import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DatePicker, { type DatePickerValue } from '@/components/ui/DatePicker'
import Pagination from '@/components/ui/Pagination'
import SearchInput from '@/components/ui/SearchInput'
import TitleRow from '@/components/ui/TitleRow'
import { useMarketEvents } from '@/features/market-events/api/queries'
import EventList from '@/features/market-events/components/EventList'
import EventTypeFilter from '@/features/market-events/components/EventTypeFilter'
import { EventsBoardSkeleton } from '@/screens/market/components/EventsBoardSkeleton'
import { loadEvents, toEventsListQuery, toEventsQuery } from '@/screens/market/EventsScreen.loader'
import { EVENTS_SEARCH_MAX_LENGTH, eventsQuerySchema } from '@/schemas/market-events'
import { pageSizeSchema } from '@/schemas/table'

/** Known without data, so the pending page draws the real one. */
const HEADER = <TitleRow title="Events" />

/**
 * The route's MAIN data is one server page of the calendar (see the loader), so the first load
 * draws one skeleton, page-wide, rather than a skeleton per row.
 */
function EventsPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading the events calendar…"
      header={HEADER}
      pageTitle="Events"
    >
      <EventsBoardSkeleton />
    </ScreenLayout>
  )
}

/** Kept light — RouteErrorPage plus the same header the pending page shows —
 *  so the frame does not change between the two: `errorComponent`s ship in
 *  the entry chunk, and a chunk that cannot load offline reloads the app. */
function EventsError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} />
}

export const Route = createFileRoute('/_app/market/events')({
  validateSearch: eventsQuerySchema,
  loader: loadEvents,
  pendingComponent: EventsPending,
  errorComponent: EventsError,
  component: EventsScreen,
})

function EventsScreen() {
  // Re-parsed through the loader's own schema rather than read straight off `Route.useSearch()`: the
  // router merges the URL's raw query string into that object, so an unrecognised param would
  // otherwise ride along into the query key.
  const search = toEventsQuery(Route.useSearch())
  const { page, pageSize, type, query, date, dateTo } = search
  const navigate = Route.useNavigate()

  // Search, type and dates are mm-be's (`q`, `type`, `start`, `end`), and so is the paging: one
  // request per page or filter, and `total` counts what matches them. The date chip opens on today,
  // as the mock's does, so the calendar a member lands on is the upcoming one.
  const events = useMarketEvents(toEventsListQuery(search))
  const rows = events.data?.rows ?? []
  const total = events.data?.total ?? 0

  // Hand-rolled 300ms debounce, the same shape StockPricesScreen already
  // uses for its own free-text field (StockPricesScreen.tsx) — see that
  // file's comment for why the guard against the mount-time "change" exists.
  const [term, setTerm] = useState(query ?? '')
  const lastCommittedQuery = useRef(query)
  useEffect(() => {
    const next = term.trim() || undefined
    if (next === lastCommittedQuery.current) return
    const id = setTimeout(() => {
      lastCommittedQuery.current = next
      void navigate({ search: (prev) => ({ ...prev, query: next, page: 1 }) })
    }, 300)
    return () => {
      clearTimeout(id)
    }
  }, [term, navigate])

  // The shared date picker (mock `Zz`, "future" mode: today on, chips Next 7 / 30 days / 3 months),
  // CTA "Show events". One day means "on or after" it, a range means "from the first to the last".
  // Fri and Sat stay pickable: events are not trading-day bound (dp Q4).
  const picked: DatePickerValue =
    date !== undefined && dateTo !== undefined
      ? { kind: 'range', from: date, to: dateTo }
      : date === undefined
        ? { kind: 'today' }
        : { kind: 'day', date }
  const dateChip = (
    <DatePicker
      value={picked}
      onChange={(next) => {
        const [from, to] =
          next.kind === 'range'
            ? [next.from, next.to]
            : [next.kind === 'day' ? next.date : undefined, undefined]
        void navigate({ search: (prev) => ({ ...prev, date: from, dateTo: to, page: 1 }) })
      }}
      mode="future"
      title="Choose a date"
      fieldLabel="Date"
      cta="Show events"
      tradingOnly={false}
      rangeAvailable
    />
  )

  return (
    <ScreenLayout header={HEADER} pageTitle="Events">
      <div className="flex flex-col gap-3 @sm:flex-row @sm:items-center @sm:justify-between">
        <SearchInput
          aria-label="Search events by company or symbol"
          placeholder="Search a company or a symbol"
          maxLength={EVENTS_SEARCH_MAX_LENGTH}
          value={term}
          onChange={(event) => {
            setTerm(event.target.value)
          }}
          className="w-full px-3 @sm:w-80"
        />

        <div className="flex flex-wrap items-center gap-3">
          <EventTypeFilter
            type={type}
            onChange={(next) => {
              void navigate({ search: (prev) => ({ ...prev, type: next, page: 1 }) })
            }}
          />
          {dateChip}
        </div>
      </div>

      {/* No `gap-*`: the list and its pager only, and `Pagination` draws
          its own 24px — a gap here would add to it. See its PLACEMENT note. */}
      <div>
        <EventList
          rows={rows}
          isLoading={events.isLoading}
          isError={events.isError}
          onRetry={() => {
            void events.refetch()
          }}
          retrying={events.isFetching}
          pageSize={pageSize}
          hasFilter={
            type !== undefined || query !== undefined || date !== undefined || dateTo !== undefined
          }
          searchQuery={query}
        />

        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          unit="events"
          onPageChange={(next) => {
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }}
          onPageSizeChange={(next) => {
            void navigate({
              search: (prev) => ({ ...prev, pageSize: pageSizeSchema.parse(next), page: 1 }),
            })
          }}
        />
      </div>
    </ScreenLayout>
  )
}
